import { useState } from "react";
import { Calendar, Clock, User, CheckCircle, XCircle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export default function LeaveApproval({
  requests = [],
  onApprove,
  currentUser,
}) {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const handleApprove = async (requestId, decision) => {
    if (!onApprove) return;

    setLoading(true);
    try {
      await onApprove(requestId, decision, comment);
      setComment("");
      setSelectedRequest(null);
    } catch (error) {
      console.error("Error approving request:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      en_attente: {
        label: "En attente",
        variant: "secondary",
        className: "bg-yellow-100 text-yellow-700",
      },
      approuve: {
        label: "Approuvé",
        variant: "default",
        className: "bg-green-100 text-green-700",
      },
      refuse: {
        label: "Refusé",
        variant: "destructive",
        className: "bg-red-100 text-red-700",
      },
      annule: {
        label: "Annulé",
        variant: "outline",
        className: "bg-gray-100 text-gray-700",
      },
    };

    const config = statusConfig[status] || statusConfig.en_attente;
    return (
      <Badge
        variant={config.variant}
        className={cn("flex items-center gap-1", config.className)}
      >
        {status === "en_attente" && <Clock className="h-3 w-3" />}
        {status === "approuve" && <CheckCircle className="h-3 w-3" />}
        {status === "refuse" && <XCircle className="h-3 w-3" />}
        {config.label}
      </Badge>
    );
  };

  if (!requests || requests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Demandes d'approbation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Aucune demande en attente</p>
            <p className="text-sm">Toutes les demandes ont été traitées</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Demandes d'approbation
          </h2>
          <p className="text-muted-foreground">
            {requests.length} demande{requests.length > 1 ? "s" : ""} en attente
            d'approbation
          </p>
        </div>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {requests.map((request) => (
          <Card key={request.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <h3 className="font-semibold">
                        {request.employe?.prenom} {request.employe?.nom}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {request.employe?.departement?.nom ||
                          "Département non spécifié"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(request.statut)}
                  {request.urgence && (
                    <Badge variant="destructive" className="text-xs">
                      Urgent
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Request Details */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Type de congé
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <div
                        className="w-3 h-3 rounded"
                        style={{
                          backgroundColor:
                            request.type_conge?.couleur || "#6B7280",
                        }}
                      />
                      <span className="font-medium">
                        {request.type_conge?.nom || "Type non spécifié"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">
                        Date de début
                      </Label>
                      <p className="mt-1 font-medium">
                        {request.date_debut
                          ? new Date(request.date_debut).toLocaleDateString(
                              "fr-FR"
                            )
                          : "Non spécifiée"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">
                        Date de fin
                      </Label>
                      <p className="mt-1 font-medium">
                        {request.date_fin
                          ? new Date(request.date_fin).toLocaleDateString(
                              "fr-FR"
                            )
                          : "Non spécifiée"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Nombre de jours
                    </Label>
                    <p className="mt-1 text-lg font-bold text-blue-600">
                      {request.nombre_jours || 0} jour
                      {request.nombre_jours > 1 ? "s" : ""}
                    </p>
                  </div>

                  {request.motif && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">
                        Motif
                      </Label>
                      <p className="mt-1 p-3 bg-muted rounded text-sm">
                        {request.motif}
                      </p>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Date de demande
                    </Label>
                    <p className="mt-1 text-sm">
                      {request.date_demande
                        ? new Date(request.date_demande).toLocaleDateString(
                            "fr-FR"
                          )
                        : "Non spécifiée"}
                    </p>
                  </div>
                </div>

                {/* Approval Actions */}
                <div className="space-y-4">
                  <div>
                    <Label
                      htmlFor={`comment-${request.id}`}
                      className="text-sm font-medium"
                    >
                      Commentaire d'approbation (optionnel)
                    </Label>
                    <Textarea
                      id={`comment-${request.id}`}
                      placeholder="Ajoutez un commentaire..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="mt-2 min-h-[100px]"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleApprove(request.id, "approuve")}
                      disabled={loading}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approuver
                    </Button>
                    <Button
                      onClick={() => handleApprove(request.id, "refuse")}
                      disabled={loading}
                      variant="destructive"
                      className="w-full"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Refuser
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
